import * as React from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { DashboardHeader } from '../components/DashboardHeader';
import { DashboardSelection } from '../components/DashboardSelection';
import BlogContent from './Blog/BlogContent';
import { AvatarSelection } from './AvatarSelection';
import { SendWishes } from './SendWishes';
import SEOContent from './SEO';
import OptimizationTable from './SEO/OptimizationTable';
import { LeadsManagement } from './LeadsManagement';
import { EditWebsiteContent } from 'features/DigitalMarketing/pages/EditWebsiteContent';
import { EDIT_WEBSITE_CONTENT_SUB_ROUTES } from 'features/DigitalMarketing/routes/editWebsiteContentRoutes';
import { UPLOAD_VIDEO_SUB_ROUTES } from 'features/DigitalMarketing/routes/uploadVideoRoutes';
import { REEL_SUB_ROUTES } from 'features/DigitalMarketing/routes/reelRoutes';
import { CREATE_POST_SUB_ROUTES } from 'features/DigitalMarketing/routes/createPostRoutes';
import { useCancelRequestsOnRouteChange } from 'features/DigitalMarketing/hooks/useCancelRequestsOnRouteChange';
import { DMDiv, DMStyle } from '../components/Primitives';
import { MarketingCommandDashboard } from './MarketingCommandDashboard';
import { LegacyMarketingDashboard } from './LegacyMarketingDashboard';

export function UpdatedDashboard() {
  const location = useLocation();
  useCancelRequestsOnRouteChange();

  const displayPathname = location.pathname;
  const isDefaultDashboard =
    displayPathname === '/digital-marketing/dashboard' ||
    displayPathname === '/digital-marketing/dashboard/';
  const isDashboardV2 =
    displayPathname === '/digital-marketing/dashboard/v2' ||
    displayPathname === '/digital-marketing/dashboard/v2/';
  const isDashboard = isDefaultDashboard || isDashboardV2;
  const isSeoView = displayPathname.includes('/digital-marketing/seo');
  const isOptimizationTable = displayPathname.includes(
    '/digital-marketing/optimization-table'
  );
  const isLeadsManagement = displayPathname.includes(
    '/digital-marketing/leads'
  );
  const isAvatarSelection = displayPathname.includes(
    '/digital-marketing/avatar-selection'
  );
  const isSendWishes = displayPathname.includes(
    '/digital-marketing/send-wishes'
  );

  React.useEffect(() => {
    if (!isDashboard) return;

    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;

    document.documentElement.classList.add('dm-dashboard-scroll-lock');
    document.body.classList.add('dm-dashboard-scroll-lock');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.classList.remove('dm-dashboard-scroll-lock');
      document.body.classList.remove('dm-dashboard-scroll-lock');
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [isDashboard]);

  return (
    <>
      <DMDiv
        as="main"
        className={`digital-marketing-route relative flex w-full flex-col bg-[#edebf7] font-inter ${
          isDashboard ? 'px-0' : 'px-[4vh]'
        } ${
          isSeoView ||
          isOptimizationTable ||
          isLeadsManagement ||
          isAvatarSelection ||
          isSendWishes
            ? 'h-full min-h-0 overflow-hidden'
            : isDashboard
            ? 'h-full min-h-0 overflow-hidden'
            : 'h-full'
        }`}
      >
        {isDashboard && (
          <DMDiv className="flex-1 h-full w-full flex flex-col min-h-0 overflow-hidden digital-marketing-route-panel">
            {isDashboardV2 ? (
              <LegacyMarketingDashboard />
            ) : (
              <MarketingCommandDashboard />
            )}
          </DMDiv>
        )}

        <DMDiv
          key={isDashboard ? 'route-hidden' : displayPathname}
          className={`min-h-0 flex-1 ${
            isDashboard
              ? 'hidden'
              : 'flex flex-col digital-marketing-route-panel'
          }`}
        >
          <Routes>
            {/* Selection Route */}
            <Route
              index
              element={
                <>
                  <DMDiv className="shrink-0">
                    <DashboardHeader
                      title="Dashboard Selection"
                      description="Navigate between dashboards to view different data insights."
                    />
                  </DMDiv>
                  <DMDiv className="flex-1 min-h-0 overflow-hidden flex flex-col">
                    <DashboardSelection />
                  </DMDiv>
                </>
              }
            />

            {/* Dashboard Route */}
            <Route path="dashboard" element={null} />
            <Route path="dashboard/v2" element={null} />

            {/* Blog Route */}
            <Route path="blog/*" element={<BlogContent />} />

            {/* SEO Route */}
            <Route path="seo" element={<SEOContent />} />
            <Route path="optimization-table" element={<OptimizationTable />} />
            <Route path="leads" element={<LeadsManagement />} />
            <Route path="leads/list" element={<LeadsManagement />} />
            <Route path="leads/create" element={<LeadsManagement />} />
            <Route path="avatar-selection" element={<AvatarSelection />} />
            <Route
              path="avatar-selection/create"
              element={<AvatarSelection />}
            />
            <Route path="send-wishes" element={<SendWishes />} />

            {/* Website Content & Videos Routes */}
            <Route
              path="edit-website-content"
              element={<EditWebsiteContent />}
            />
            {EDIT_WEBSITE_CONTENT_SUB_ROUTES.map(route => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}
            {UPLOAD_VIDEO_SUB_ROUTES.map(route => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}
            {REEL_SUB_ROUTES.map(route => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}
            {CREATE_POST_SUB_ROUTES.map(route => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}

            {/* Fallback */}
            <Route
              path="*"
              element={<Navigate to="/digital-marketing" replace />}
            />
          </Routes>
        </DMDiv>

        <DMStyle>
          {`
                        .digital-marketing-route-panel {
                            animation: digital-marketing-route-panel 260ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
                        }

                        @keyframes digital-marketing-route-panel {
                            0% { opacity: 0; transform: translateY(0.8vh); }
                            100% { opacity: 1; transform: translateY(0); }
                        }
                    `}
        </DMStyle>
      </DMDiv>
    </>
  );
}
